'use client';

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  CustomHeroButton,
  ResponsiveModal,
  ResponsiveTooltip,
  Typography,
} from '../../../components';
import FiltersSection from './components/FiltersSection';
import ProposalsTable from './components/ProposalsTable';
import PartnerLevelProgress from './components/PartnerLevelProgress';
import PartnerLevelModal from './components/PartnerLevelModal';
import { ProposalData, ProposalsFilters } from './types';
import { transformProposalOfferToProposalData } from './utils';
import { DEFAULT_FILTERS, DEFAULT_PAGE_SIZE, SEARCH_DEBOUNCE_DELAY } from './constants';
import clientCplService, { CplRequestParams } from '../../../api/client/clientCplService';
import { RefreshIc, ResetFiltersIc } from '../../../components/icons';
import { usePageBreadcrumbs } from '../../../hooks/usePageBreadcrumbs';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  useDisclosure,
  addToast,
  Tooltip,
} from '@heroui/react';
import { useAccount } from '../../../contexts/AccountContext';
import { useNavigate } from 'react-router';
import { useTranslations } from '../../../contexts/translation.context';
import { showCreatorSuccess, showError } from '../../../utils/toast';
import { clientAuthService } from '../../../api';
import clientStatisticService from '../../../api/client/clientStatisticService';
import { useAuth } from '../../../contexts/AuthContext';

const Proposals: React.FC = () => {
  const { t } = useTranslations();
  const { user } = useAuth();
  const [proposals, setProposals] = useState<ProposalData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalCount, setTotalCount] = useState(0);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { accounts } = useAccount();
  const [filters, setFilters] = useState<ProposalsFilters>(DEFAULT_FILTERS);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [priceError, setPriceError] = useState(0);
  const [favoriteIds, setFavoriteIds] = useState<string[]>([]);
  const [isLevelModalOpen, setIsLevelModalOpen] = useState(false);
  const [clientLvl, setClientLvl] = useState<any>(null);
  const [currentMonthStats, setCurrentMonthStats] = useState<any>(null);
  const breadcrumbs = useMemo(
    () => [
      { label: t('proposals.home'), href: '/' },
      { label: t('proposals.proposals'), isActive: true },
    ],
    [t]
  );
  usePageBreadcrumbs(breadcrumbs);
  const navigate = useNavigate();

  // Определяем порядок уровней
  const levelOrder = ['bronze', 'silver', 'gold', 'platinum', 'diamond'];

  // Определяем массив месяцев для переводов
  const monthKeys = [
    'january',
    'february',
    'march',
    'april',
    'may',
    'june',
    'july',
    'august',
    'september',
    'october',
    'november',
    'december',
  ];

  // Вычисляем текущий и предыдущий месяц
  const currentMonthKey = useMemo(() => {
    const currentMonth = new Date().getMonth(); // 0-11
    return monthKeys[currentMonth];
  }, []);

  const previousMonthKey = useMemo(() => {
    const currentMonth = new Date().getMonth(); // 0-11
    const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    return monthKeys[prevMonth];
  }, []);

  // Вычисляем данные для прогресса и модального окна
  const levelData = useMemo(() => {
    if (!user || !clientLvl || !user.lvl) {
      return null;
    }

    const currentLevelKey = user.lvl;
    const currentLevelIndex = levelOrder.indexOf(currentLevelKey);
    const nextLevelIndex = currentLevelIndex + 1;
    const hasNextLevel = nextLevelIndex < levelOrder.length;
    const nextLevelKey = hasNextLevel ? levelOrder[nextLevelIndex] : currentLevelKey;

    const currentCommission = clientLvl[currentLevelKey]?.commition || 0;
    const nextCommission = hasNextLevel ? clientLvl[nextLevelKey]?.commition : currentCommission;
    //@ts-ignore
    const currentAmount = parseFloat(currentMonthStats?.holden_sum - currentMonthStats?.refunded_sum || '0');
    const targetAmount = hasNextLevel
      ? clientLvl[nextLevelKey]?.cost || 0
      : clientLvl[currentLevelKey]?.cost || 0;
    const currentLevelCost = clientLvl[currentLevelKey]?.cost || 0;

    // Вычисляем прогресс относительно диапазона между текущим и следующим уровнем
    const amountInCurrentLevel = currentAmount - currentLevelCost;
    const rangeToNextLevel = targetAmount - currentLevelCost;
    const progress =
      hasNextLevel && rangeToNextLevel > 0
        ? Math.min(Math.max((amountInCurrentLevel / rangeToNextLevel) * 100, 0), 100)
        : 100;

    const amountToNextLevel = hasNextLevel ? Math.max(targetAmount - currentAmount, 0) : 0;

    return {
      currentLevel: currentLevelKey,
      currentCommission: `${currentCommission}%`,
      nextLevel: nextLevelKey,
      nextCommission: `${nextCommission}%`,
      currentAmount,
      targetAmount,
      progress,
      amountToNextLevel: `$${amountToNextLevel.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      hasNextLevel,
    };
  }, [user, clientLvl, currentMonthStats]);

  const [localSearch, setLocalSearch] = useState(filters.search);

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: localSearch }));
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [localSearch]);

  useEffect(() => {
    setLocalSearch(filters.search);
  }, [filters.search]);

  const apiParams = useMemo(() => {
    const params: CplRequestParams = {
      page: currentPage,
      page_size: pageSize,
    };

    if (filters.search) {
      (params as any).search = filters.search;
    }

    if (filters.source !== 'all') {
      params.source = filters.source as any;
    }

    if (filters.sort !== 'all') {
      params.sort = filters.sort;
    }

    if (filters.featured) {
      (params as any).featured = filters.featured;
    }

    if (filters.suitable) {
      (params as any).suitable = filters.suitable;
    }

    if (filters.offer_type && filters.offer_type !== 'all') {
      params.cpl_offer_type = filters.offer_type;
    }

    if (filters.campaign_type && filters.campaign_type !== 'all') {
      (params as any).campaign_type = filters.campaign_type;
    }

    if (filters.partner_level && filters.partner_level !== 'all') {
      (params as any).partner_level = filters.partner_level;
    }

    if (filters.price_min !== null) {
      (params as any).price_min = filters.price_min;
    }

    if (filters.price_max !== null) {
      (params as any).price_max = filters.price_max;
    }

    return params;
  }, [
    currentPage,
    pageSize,
    filters.search,
    filters.source,
    filters.sort,
    filters.featured,
    filters.suitable,
    filters.offer_type,
    filters.campaign_type,
    filters.partner_level,
    filters.price_min,
    filters.price_max,
  ]);
  const loadProposals = async () => {
    setIsLoading(true);
    try {
      const [proposalsResult, lvlResult, monthStatsResult] = await Promise.allSettled([
        clientCplService.getProposals(apiParams),
        clientAuthService.getClientLvl(),
        clientStatisticService.getCurrentMonthStats(),
      ]);

      if (
        proposalsResult.status === 'fulfilled' &&
        proposalsResult.value &&
        proposalsResult.value.objects &&
        Array.isArray(proposalsResult.value.objects)
      ) {
        const transformedProposals = proposalsResult.value.objects.map((offer) => {
          return transformProposalOfferToProposalData(offer, t);
        });

        setProposals(transformedProposals);
        setTotalCount(proposalsResult.value.count || 0);
      } else {
        if (proposalsResult.status === 'rejected') {
          console.error('Error loading proposals:', proposalsResult.reason);
        }
        setProposals([]);
        setTotalCount(0);
      }

      if (lvlResult.status === 'fulfilled') {
        setClientLvl(lvlResult.value?.data || null);
      } else if (lvlResult.status === 'rejected') {
        console.error('Error loading client level:', lvlResult.reason);
      }

      if (monthStatsResult.status === 'fulfilled') {
        setCurrentMonthStats(monthStatsResult.value?.data || null);
      } else if (monthStatsResult.status === 'rejected') {
        console.error('Error loading current month stats:', monthStatsResult.reason);
      }
    } catch (error) {
      console.error('Error loading proposals:', error);
      setProposals([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  const loadFavorites = async () => {
    try {
      const response = await clientCplService.getFavorites();
      setFavoriteIds(response.offers || []);
    } catch (error) {
      console.error('Error loading favorites:', error);
      setFavoriteIds([]);
    }
  };

  const handleToggleFavorite = async (proposalId: string) => {
    try {
      const isFavorite = favoriteIds.includes(proposalId);

      if (isFavorite) {
        await clientCplService.removeFromFavorites(proposalId);
        setFavoriteIds((prev) => prev.filter((id) => id !== proposalId));

        showCreatorSuccess(t('proposals.removedFromFavorites'));
      } else {
        await clientCplService.addToFavorites(proposalId);
        setFavoriteIds((prev) => [...prev, proposalId]);

        showCreatorSuccess(t('proposals.addedToFavorites'));
      }
      await loadProposals();
    } catch (error) {
      console.error('Error toggling favorite:', error);

      showError(t('proposals.favoriteError'));
    }
  };

  useEffect(() => {
    loadFavorites();
  }, []);

  useEffect(() => {
    loadProposals();
  }, [apiParams]);

  const handleFilterChange = useCallback(
    (key: keyof ProposalsFilters, value: string | string[] | number | boolean | null) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
      setCurrentPage(1);
    },
    []
  );

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  }, []);

  const handleOrderProposal = useCallback((proposal: ProposalData) => {}, []);

  const handleResetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setLocalSearch('');
    setCurrentPage(1);
  }, []);

  // Проверяем, есть ли активные фильтры
  const hasActiveFilters = useMemo(() => {
    return (
      filters.search !== '' ||
      filters.sort !== 'all' ||
      filters.source !== 'all' ||
      filters.featured !== false ||
      filters.suitable !== false ||
      filters.offer_type !== 'all' ||
      filters.campaign_type !== 'all' ||
      filters.partner_level !== 'all' ||
      filters.price_min !== null ||
      filters.price_max !== null
    );
  }, [filters]);

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="px-4 py-6 md:py-4 md:px-6">
        <Typography variant="h5" className="md:font-semibold text-gray-900">
          {t('proposals.proposals')}
        </Typography>
      </div>

      <div className="px-4 pb-6 md:px-6 md:pb-6 flex md:flex-row flex-col-reverse md:items-center md:flex-wrap md:justify-between gap-6 md:gap-4 w-full">
        <div className="flex items-center gap-4 max-md:h-10 [&>*]:shrink-0 max-md:overflow-x-auto max-md:px-4 max-md:-mx-4 scroller-none">
          <FiltersSection
            filters={filters}
            localSearch={localSearch}
            sortDirection={sortDirection}
            onSearchChange={setLocalSearch}
            onFilterChange={handleFilterChange}
            onSortDirectionChange={setSortDirection}
          />
          {!hasActiveFilters && <div className="shrink-0 w-10 h-2 md:hidden"></div>}
          {hasActiveFilters && (
            <Tooltip content={t('proposals.resetFilters')} placement="top">
              <Button
                //@ts-ignore
                size="xs"
                className="h-9 rounded-2 !w-9 p-0 bg-auth-surface shrink-0"
                onPress={handleResetFilters}
              >
                <ResetFiltersIc />
              </Button>
            </Tooltip>
          )}
        </div>

        {levelData && (
          <div onClick={() => setIsLevelModalOpen(true)} className="cursor-pointer">
            <PartnerLevelProgress
              currentMonth={t(`proposals.months.${previousMonthKey}`)}
              currentLevel={t(`proposals.partnerLevels.${levelData.currentLevel}`)}
              currentLevelKey={levelData.currentLevel}
              commission={levelData.currentCommission}
              nextMonth={t(`proposals.months.${currentMonthKey}`)}
              currentAmount={levelData.currentAmount}
              targetAmount={levelData.targetAmount}
              nextLevel={t(`proposals.partnerLevels.${levelData.nextLevel}`)}
              progress={levelData.progress}
              t={t}
            />
          </div>
        )}
      </div>

      {/* Таблица */}
      <div className="px-4 md:px-6 md:h-full">
        <ProposalsTable
          openModal={(price: number) => {
            setPriceError(price);
            onOpen();
          }}
          proposals={proposals}
          isLoading={isLoading}
          totalCount={totalCount}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onOrderProposal={handleOrderProposal}
          favoriteIds={favoriteIds}
          onToggleFavorite={handleToggleFavorite}
        />
      </div>
      <ResponsiveModal isOpen={isOpen} onClose={onClose}>
        <ModalContent className="md:w-[25rem]">
          <ModalHeader className="flex items-center gap-2">
            {accounts?.length > 0 ? (
              <>
                <h3 className="text-lg font-medium">{t('proposals.insufficientFunds')}</h3>
              </>
            ) : (
              <>
                <h3 className="text-lg font-medium">{t('proposals.addOnlyFansAccount')}</h3>
              </>
            )}
          </ModalHeader>
          <ModalBody className="pt-3 px-4 md:px-6 pb-6">
            <Typography variant="body2">
              {accounts?.length > 0 ? (
                <Typography variant="body2">
                  {t('proposals.insufficientBalance', { price: priceError })}
                </Typography>
              ) : (
                t('proposals.noPaidAccount')
              )}
            </Typography>
          </ModalBody>
          <ModalFooter>
            <CustomHeroButton
              className="!text-auth-strength-weak !bg-auth-strength-weak/20"
              onPress={onClose}
              title={t('proposals.close')}
            />
            <CustomHeroButton
              onPress={() =>
                accounts?.length > 0
                  ? navigate('/client/account/wallet')
                  : navigate('/client/accounts/create')
              }
              title={accounts?.length > 0 ? t('proposals.continue') : t('proposals.add')}
            />
          </ModalFooter>
        </ModalContent>
      </ResponsiveModal>

      {levelData && (
        <PartnerLevelModal
          isOpen={isLevelModalOpen}
          onClose={() => setIsLevelModalOpen(false)}
          currentLevel={t(`proposals.partnerLevels.${levelData.currentLevel}`)}
          currentLevelKey={levelData.currentLevel}
          currentCommission={levelData.currentCommission}
          nextLevel={t(`proposals.partnerLevels.${levelData.nextLevel}`)}
          nextCommission={levelData.nextCommission}
          amountToNextLevel={levelData.amountToNextLevel}
          levels={clientLvl}
          t={t}
        />
      )}
    </div>
  );
};

export default Proposals;
